import React, { useEffect, useRef, useState } from 'react';
import { startCustomerCall, checkCustomerCall, loginCustomer, completeCustomerPassword } from '../services/customerService';
import { currentCall, callForPhone, saveCall, forgetCall, newCall, normalizeCallPhone } from '../utils/customerCallContinuation';
import '../styles/customerCallLogin.css';

const left = (deadline, now) => Math.max(0, Math.ceil((Date.parse(deadline) - now) / 1000)) || 0;
const clock = seconds => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
export default function CustomerCallLogin({ onAuthenticated, initialMode = 'login' }) {
  const [challenge, setChallenge] = useState(currentCall);
  const [phone, setPhone] = useState(() => currentCall()?.phone || '');
  const [mode, setMode] = useState(() => currentCall() ? 'register' : initialMode);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retryAt, setRetryAt] = useState('');
  const [now, setNow] = useState(Date.now);
  const callback = useRef(onAuthenticated), latest = useRef(challenge);
  const submitting = useRef(false);
  callback.current = onAuthenticated; latest.current = challenge;
  const id = challenge?.challengeId, secret = challenge?.secret;
  const remaining = challenge ? left(challenge.expiresAt, now) : 0;
  const expired = challenge && (!remaining || challenge.status === 'expired');
  const failed = challenge?.status === 'failed';
  const retryRemaining = left(failed ? challenge.retryAt : retryAt, now);
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, []);
  useEffect(() => {
    if (!id) return undefined;
    let active = true, timer;
    const apply = result => {
      const updated = { ...latest.current, ...result };
      saveCall(updated); setChallenge(updated);
    };
    const poll = async () => {
      const current = latest.current;
      if (!active || current?.challengeId !== id || !left(current.expiresAt, Date.now()) || ['failed', 'expired', 'confirmed'].includes(current.status)) return;
      if (!document.hidden) {
        try {
          const result = await checkCustomerCall({ challengeId: id, secret });
          if (!active) return;
          apply(result);
          setError(result.temporaryError ? 'Не удалось проверить звонок. Повторим проверку автоматически.' : '');
          if (['expired', 'failed', 'confirmed'].includes(result.status)) return;
        } catch (e) {
          if (!active) return;
          if ([404, 410].includes(e.response?.status) && Date.now() - (current.startedAt || 0) > 15000) {
            apply({ status: 'expired' }); setError(''); return;
          }
          setError('Восстанавливаем проверку. Новый запрос на звонок не создаётся.');
        }
      }
      if (active) timer = setTimeout(poll, 5000);
    };
    timer = setTimeout(poll, 1000);
    return () => { active = false; clearTimeout(timer); };
  }, [id, secret]);

  const start = async event => {
    event?.preventDefault();
    if (busy || retryRemaining) return;
    const normalized = normalizeCallPhone(phone);
    if (!normalized) { setError('Введите российский номер: +7 и 10 цифр'); return; }
    setBusy(true); setError(''); setRetryAt('');
    let continuation, persisted = false;
    try {
      const previous = callForPhone(normalized);
      continuation = previous && !(previous.status === 'failed' && !left(previous.retryAt, Date.now())) ? previous : newCall(normalized);
      saveCall(continuation); // Persist capability BEFORE sending a possibly paid request.
      persisted = true;
      setPhone(normalized); setChallenge(continuation);
      const result = await startCustomerCall(normalized, continuation);
      const updated = { ...continuation, ...result };
      saveCall(updated); setChallenge(updated);
    } catch (e) {
      const status = e.response?.status;
      if (continuation && persisted && ![400, 403, 429].includes(status)) {
        const pending = { ...continuation, status: 'uncertain' };
        saveCall(pending); setChallenge(pending);
        setError('Ответ не получен. Восстанавливаем проверку автоматически.');
      } else {
        if (continuation && persisted) forgetCall(continuation);
        setChallenge(null);
        const seconds = Number(e.response?.data?.retryAfterSeconds || e.response?.headers?.['retry-after']);
        if (Number.isFinite(seconds) && seconds > 0 && seconds <= 86400) setRetryAt(new Date(Date.now() + seconds * 1000).toISOString());
        setError(e.response?.data?.error || (!persisted ? 'Не удалось сохранить проверку. Разрешите сохранение данных сайта в браузере.' : 'Не удалось начать проверку. Попробуйте позже.'));
      }
    } finally { setBusy(false); }
  };
  const changePhone = () => { setChallenge(null); setPassword(''); setConfirmation(''); setError(''); setRetryAt(''); }; // Keep continuation for returning to the same number.
  const switchMode = next => { changePhone(); setMode(next); setShowPassword(false); };
  const submitPassword = async event => {
    event.preventDefault();
    if (submitting.current) return;
    const normalized = normalizeCallPhone(phone);
    if (!normalized) { setError('Введите российский номер: +7 и 10 цифр'); return; }
    if (challenge && password !== confirmation) { setError('Пароли не совпадают'); return; }
    submitting.current = true; setBusy(true); setError('');
    try {
      const session = challenge ? await completeCustomerPassword(challenge, password) : await loginCustomer(normalized, password);
      if (challenge) { forgetCall(challenge); setChallenge(null); }
      setPassword(''); setConfirmation('');
      await callback.current(session);
    } catch (e) { setError(e.response?.data?.error || 'Не удалось войти. Если пароль уже сохранён, попробуйте вход по номеру и паролю.'); }
    finally { submitting.current = false; setBusy(false); }
  };
  const passwordField = (isNew = false) => <>
    <label htmlFor="customer-password">{isNew ? 'Новый пароль' : 'Пароль'}</label>
    <input id="customer-password" className="profile-input" type={showPassword ? 'text' : 'password'} autoComplete={isNew ? 'new-password' : 'current-password'} minLength={8} maxLength={64} value={password} onChange={e => setPassword(e.target.value)} required disabled={busy} aria-describedby={isNew ? 'customer-password-help' : undefined} />
    <button type="button" className="customer-login-link" aria-pressed={showPassword} onClick={() => setShowPassword(value => !value)}>{showPassword ? 'Скрыть пароль' : 'Показать пароль'}</button>
    {isNew && <><p id="customer-password-help">От 8 до 64 символов. При следующем входе понадобятся только номер и пароль.</p>
      <label htmlFor="customer-password-confirm">Повторите пароль</label>
      <input id="customer-password-confirm" className="profile-input" type={showPassword ? 'text' : 'password'} autoComplete="new-password" minLength={8} maxLength={64} value={confirmation} onChange={e => setConfirmation(e.target.value)} required disabled={busy} />
    </>}
  </>;
  return (
    <section className="customer-call-login" aria-labelledby="customer-call-title">
      <h1 id="customer-call-title">{challenge?.status === 'confirmed' && !expired ? 'Создайте пароль' : mode === 'login' ? 'Вход в MaxMarket' : mode === 'reset' ? 'Восстановление пароля' : 'Регистрация'}</h1>
      <p>Личный кабинет покупателя</p>
      {error && <p role="alert" className="profile-error">{error}</p>}
      {!challenge && mode === 'login' ? <form onSubmit={submitPassword}>
        <label htmlFor="customer-login-phone">Номер телефона</label>
        <input id="customer-login-phone" className="profile-input" type="tel" autoComplete="username" inputMode="tel" maxLength={32} value={phone} onChange={e => setPhone(e.target.value)} placeholder="+7 (900) 000-00-00" required disabled={busy} />
        {passwordField()}
        <button type="submit" className="profile-btn" disabled={busy}>{busy ? 'Входим…' : 'Войти'}</button>
        <button type="button" className="customer-login-link" disabled={busy} onClick={() => switchMode('reset')}>Забыли пароль?</button>
        <button type="button" className="profile-btn profile-btn--outline" disabled={busy} onClick={() => switchMode('register')}>Зарегистрироваться / задать пароль</button>
        <a className="customer-call-privacy" href="/privacy">Обработка персональных данных</a>
      </form> : !challenge ? (
        <form onSubmit={start}>
          <label htmlFor="customer-call-phone">Номер телефона</label>
          <input id="customer-call-phone" className="profile-input" type="tel" autoComplete="tel" inputMode="tel" maxLength={32} value={phone} onChange={e => setPhone(e.target.value)} placeholder="+7 (900) 000-00-00" required disabled={busy} aria-describedby="customer-call-help" />
          <p id="customer-call-help">Покажем номер, на который нужно позвонить со своего телефона.</p>
          <button type="submit" className="profile-btn" disabled={busy || retryRemaining > 0}>{busy ? 'Готовим проверку…' : retryRemaining ? `Повторить через ${clock(retryRemaining)}` : 'Подтвердить номер звонком'}</button>
          <a className="customer-call-privacy" href="/privacy">Обработка персональных данных</a>
          <button type="button" className="customer-login-link" disabled={busy} onClick={() => switchMode('login')}>Уже есть пароль — войти</button>
        </form>
      ) : (
        <div>
          {expired ? <p role="status">Время подтверждения истекло или проверка уже завершена. Начните вход заново.</p> : failed ? (
            <p role="status">Не удалось получить номер для звонка. Новая попытка доступна {retryRemaining ? `через ${clock(retryRemaining)}` : 'сейчас'}.</p>
          ) : challenge.status === 'confirmed' ? <form onSubmit={submitPassword}>
            <p role="status">Номер <strong>{challenge.phone}</strong> подтверждён.</p>
            {passwordField(true)}
            <button type="submit" className="profile-btn" disabled={busy}>{busy ? 'Сохраняем…' : 'Сохранить пароль и войти'}</button>
            <p className="customer-call-timer">Завершите за {clock(remaining)}.</p>
          </form> : challenge.status === 'waiting' && challenge.callPhone ? (
            <>
              <p>Позвоните с номера <strong>{challenge.phone}</strong> на:</p>
              <a className="customer-call-number" href={`tel:${challenge.callPhone}`} aria-label={`Позвонить на ${challenge.callPhonePretty}`}>{challenge.callPhonePretty}</a>
              <p>Сервис сбросит звонок. Дождитесь подтверждения на этой странице.</p>
              <p role="status" aria-live="polite">Ждём ваш звонок…</p>
              <p className="customer-call-timer">Осталось {clock(remaining)}</p>
            </>
          ) : (
            <>
              <p role="status">{challenge.status === 'uncertain' ? 'Проверяем результат запроса. Если номер не появится, дождитесь окончания этой проверки.' : 'Получаем номер для звонка…'}</p>
              <p>Можно обновить страницу — проверка сохранится.</p>
              <p className="customer-call-timer">Осталось {clock(remaining)}</p>
            </>
          )}
          {(failed || expired) && <button type="button" className="profile-btn" disabled={busy || (failed && retryRemaining > 0)} onClick={start}>{failed && retryRemaining ? `Повторить через ${clock(retryRemaining)}` : 'Начать заново'}</button>}
          <button type="button" className="profile-btn profile-btn--outline" disabled={busy} onClick={changePhone}>Изменить номер</button>
          <button type="button" className="customer-login-link" disabled={busy} onClick={() => switchMode('login')}>Войти по номеру и паролю</button>
        </div>
      )}
    </section>
  );
}
